import { customRef, type Ref } from 'vue';

// Define a union type for all possible focusable HTML elements
type FocusableElement = HTMLElement | HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const focusableElementsSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Composable function to implement a focus trap within a DOM element.
 * It ensures that the Tab key only cycles focus among elements inside the trap.
 * * @returns An object containing the trapRef (to be set on the container element) and cleanup functions.
 */
export const useFocusTrap = () => {
    // Array to hold the list of focusable elements found within the trap container
    let focusableElements: NodeListOf<FocusableElement> | [] = [];

    // Refs to hold the first and last focusable elements for trapping logic
    let $firstFocusable: FocusableElement | null = null;
    let $lastFocusable: FocusableElement | null = null;

    let isInitialized = false;

    // -----------------------------------------------------------
    // 1. trapRef (customRef for the DOM element)
    // The type parameter for customRef is the type of the value it holds: HTMLElement or null.
    // -----------------------------------------------------------
    const trapRef: Ref<HTMLElement | null> = customRef((track, trigger) => {
        let $trapEl: HTMLElement | null = null;
        return {
            get() {
                track();
                return $trapEl;
            },
            set(value: HTMLElement | null) {
                $trapEl = value;
                // Use the value's presence to determine initialization or cleanup
                value ? (isInitialized ? null : initFocusTrap()) : clearFocusTrap();
                trigger();
            },
        };
    });

    // -----------------------------------------------------------
    // 2. Event Handler Logic
    // -----------------------------------------------------------
    function keyHandler(e: KeyboardEvent): void {
        const isTabPressed = e.key === 'Tab';

        if (!isTabPressed || !$firstFocusable || !$lastFocusable) {
            return;
        }

        if (e.shiftKey) {
            // Shift + Tab (Backward cycling)
            if (document.activeElement === $firstFocusable) {
                $lastFocusable.focus();
                e.preventDefault();
            }
        } else {
            // Tab (Forward cycling)
            if (document.activeElement === $lastFocusable) {
                $firstFocusable.focus();
                e.preventDefault();
            }
        }
    }

    // -----------------------------------------------------------
    // 3. Initialization and Cleanup
    // -----------------------------------------------------------
    function initFocusTrap(): void {
        // Bail out if trapRef is null or its value is not a DOM element
        if (!trapRef.value) return;

        // Type casting the result of querySelectorAll is necessary for stricter TS environments
        focusableElements = trapRef.value.querySelectorAll<FocusableElement>(focusableElementsSelector);

        // Ensure elements were found before assigning and focusing
        if (focusableElements.length === 0) return;

        $firstFocusable = focusableElements[0];
        $lastFocusable = focusableElements[focusableElements.length - 1];

        document.addEventListener('keydown', keyHandler);

        // Set initial focus to the first element
        $firstFocusable.focus();
        isInitialized = true;
    }

    function clearFocusTrap(): void {
        document.removeEventListener('keydown', keyHandler);
        $firstFocusable = null;
        $lastFocusable = null;
        focusableElements = [];
        isInitialized = false;
    }

    return {
        trapRef,
        initFocusTrap,
        clearFocusTrap,
    };
};
